/**
 * Pagina Calendario Appuntamenti
 * Gestione completa degli appuntamenti con calendario settimanale interattivo
 */

import React, { useState, useEffect, useRef } from 'react';
import './CalendarPage.css';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Edit,
  Visibility,
  Refresh,
  Settings as SettingsIcon,
  ZoomIn,
  ZoomOut,
  Print
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import itLocale from '@fullcalendar/core/locales/it';
import calendarService, { BookingEvent, CalendarFilters } from '../services/calendarService';
import BookingDetailsModal from '../components/calendar/BookingDetailsModal';
import BookingEditModal from '../components/calendar/BookingEditModal';
import BookingConfirmModal from '../components/calendar/BookingConfirmModal';

const CalendarPage: React.FC = () => {
  // State per il calendario
  const calendarRef = useRef<any>(null);
  const [events, setEvents] = useState<BookingEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // State per i filtri
  const [diaryIds, setDiaryIds] = useState('9025125');
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(getWeekStart(new Date()));

  // State per le modali
  const [selectedEvent, setSelectedEvent] = useState<BookingEvent | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // State per la modalità modifica
  const [editMode, setEditMode] = useState(false);

  // State per zoom/slot duration
  const [slotDuration, setSlotDuration] = useState<string>('00:15:00'); // 15min default

  // State per conferma modifiche
  const [pendingChange, setPendingChange] = useState<{
    type: 'move' | 'create' | 'delete';
    oldData?: BookingEvent;
    newData: Partial<BookingEvent>;
  } | null>(null);

  /**
   * Calcola l'inizio della settimana (lunedì)
   */
  function getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Lunedì
    return new Date(d.setDate(diff));
  }

  /**
   * Calcola la fine della settimana (domenica)
   */
  function getWeekEnd(weekStart: Date): Date {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return d;
  }

  /**
   * Carica gli appuntamenti dal backend
   */
  const loadBookings = async () => {
    if (!diaryIds.trim()) {
      setError('Inserisci almeno un ID agenda');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const weekEnd = getWeekEnd(currentWeekStart);

      const filters: CalendarFilters = {
        diaryIds: diaryIds.trim(),
        fromDate: calendarService.formatDateForFilter(currentWeekStart),
        toDate: calendarService.formatDateForFilter(weekEnd),
        view: 'ultraverbose'
      };

      const bookings = await calendarService.getBookings(filters);
      setEvents(bookings);
      setSuccess(`Caricati ${bookings.length} appuntamenti`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento degli appuntamenti');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Carica gli appuntamenti al montaggio e quando cambia la settimana
   */
  useEffect(() => {
    loadBookings();
  }, [currentWeekStart]);

  /**
   * Aggiorna la data del calendario quando cambia la settimana
   */
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(currentWeekStart);
    }
  }, [currentWeekStart]);

  /**
   * Naviga alla settimana precedente
   */
  const handlePrevWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);
  };

  /**
   * Naviga alla settimana successiva
   */
  const handleNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);
  };

  /**
   * Torna alla settimana corrente
   */
  const handleToday = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
  };

  /**
   * Gestisce lo zoom in (slot più piccoli)
   */
  const handleZoomIn = () => {
    if (slotDuration === '01:00:00') {
      setSlotDuration('00:30:00');
    } else if (slotDuration === '00:30:00') {
      setSlotDuration('00:15:00');
    } else if (slotDuration === '00:15:00') {
      setSlotDuration('00:10:00');
    } else if (slotDuration === '00:10:00') {
      setSlotDuration('00:05:00');
    }
  };

  /**
   * Gestisce lo zoom out (slot più grandi)
   */
  const handleZoomOut = () => {
    if (slotDuration === '00:05:00') {
      setSlotDuration('00:10:00');
    } else if (slotDuration === '00:10:00') {
      setSlotDuration('00:15:00');
    } else if (slotDuration === '00:15:00') {
      setSlotDuration('00:30:00');
    } else if (slotDuration === '00:30:00') {
      setSlotDuration('01:00:00');
    }
  };

  /**
   * Ottieni label leggibile per la durata slot
   */
  const getSlotDurationLabel = () => {
    switch (slotDuration) {
      case '00:05:00': return '5 min';
      case '00:10:00': return '10 min';
      case '00:15:00': return '15 min';
      case '00:30:00': return '30 min';
      case '01:00:00': return '1 ora';
      default: return slotDuration;
    }
  };

  /**
   * Gestisce il click su un evento (doppio click)
   */
  const handleEventClick = (clickInfo: any) => {
    const event = events.find(e => e.id === clickInfo.event.id);
    if (event) {
      setSelectedEvent(event);
      if (editMode) {
        setEditModalOpen(true);
      } else {
        setDetailsModalOpen(true);
      }
    }
  };

  /**
   * Gestisce il drag & drop di un evento
   */
  const handleEventDrop = (dropInfo: any) => {
    if (!editMode) {
      dropInfo.revert();
      return;
    }

    const event = events.find(e => e.id === dropInfo.event.id);
    if (!event) {
      dropInfo.revert();
      return;
    }

    // Prepara i dati per la conferma
    setPendingChange({
      type: 'move',
      oldData: event,
      newData: {
        ...event,
        start: dropInfo.event.start,
        end: dropInfo.event.end
      }
    });
    setConfirmModalOpen(true);

    // Ripristina visivamente fino a conferma
    dropInfo.revert();
  };

  /**
   * Gestisce il resize di un evento
   */
  const handleEventResize = (resizeInfo: any) => {
    if (!editMode) {
      resizeInfo.revert();
      return;
    }

    const event = events.find(e => e.id === resizeInfo.event.id);
    if (!event) {
      resizeInfo.revert();
      return;
    }

    setPendingChange({
      type: 'move',
      oldData: event,
      newData: {
        ...event,
        start: resizeInfo.event.start,
        end: resizeInfo.event.end
      }
    });
    setConfirmModalOpen(true);

    resizeInfo.revert();
  };

  /**
   * Gestisce la selezione di uno slot (per creare nuovo evento)
   */
  const handleDateSelect = (selectInfo: any) => {
    if (!editMode) return;

    // Crea un nuovo evento temporaneo
    setPendingChange({
      type: 'create',
      newData: {
        start: selectInfo.start,
        end: selectInfo.end,
        diaryId: diaryIds.split(',')[0].trim() // Prima agenda dalla lista
      }
    });
    setEditModalOpen(true);
  };

  /**
   * Conferma la modifica/creazione/cancellazione
   */
  const handleConfirmChange = async () => {
    if (!pendingChange) return;

    setLoading(true);
    setError(null);

    try {
      if (pendingChange.type === 'move' && pendingChange.oldData) {
        // Sposta l'appuntamento
        await calendarService.moveBooking({
          bookingId: pendingChange.oldData.bookingId,
          newDiaryId: pendingChange.oldData.diaryId,
          newTimeSlot: calendarService.formatDateForApi(pendingChange.newData.start as Date),
          reason: 'Spostamento da calendario'
        });
        setSuccess('Appuntamento spostato con successo');
      } else if (pendingChange.type === 'delete' && pendingChange.oldData) {
        // Cancella l'appuntamento
        await calendarService.updateBookingStatus({
          bookingId: pendingChange.oldData.bookingId,
          status: 'CANCELLED',
          reason: 'Cancellazione da calendario'
        });
        setSuccess('Appuntamento cancellato con successo');
      }

      // Ricarica gli appuntamenti
      await loadBookings();
      setConfirmModalOpen(false);
      setPendingChange(null);
    } catch (err: any) {
      setError(err.message || 'Errore nell\'operazione');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Salva un appuntamento modificato/creato
   */
  const handleSaveBooking = async (bookingData: any) => {
    setLoading(true);
    setError(null);

    try {
      if (bookingData.bookingId) {
        // Modifica esistente
        await calendarService.moveBooking({
          bookingId: bookingData.bookingId,
          newDiaryId: bookingData.diaryId,
          newTimeSlot: calendarService.formatDateForApi(new Date(bookingData.start)),
          reason: 'Modifica da calendario'
        });
        setSuccess('Appuntamento modificato con successo');
      } else {
        // Nuovo appuntamento
        await calendarService.createBooking({
          patientId: bookingData.patientId,
          diaryId: bookingData.diaryId,
          serviceCode: bookingData.serviceCode,
          timeSlot: calendarService.formatDateForApi(new Date(bookingData.start)),
          priority: bookingData.priority || 'NORMAL',
          notes: bookingData.notes
        });
        setSuccess('Appuntamento creato con successo');
      }

      setEditModalOpen(false);
      setPendingChange(null);
      await loadBookings();
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Elimina un appuntamento
   */
  const handleDeleteBooking = (booking: BookingEvent) => {
    setPendingChange({
      type: 'delete',
      oldData: booking,
      newData: {}
    });
    setEditModalOpen(false);
    setConfirmModalOpen(true);
  };

  /**
   * Stampa il calendario per il range di date corrente
   */
  const handlePrintCalendar = () => {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);

    // Filtra gli eventi per il range corrente
    const eventsInRange = events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate >= currentWeekStart && eventDate < weekEnd;
    });

    // Crea il contenuto HTML per la stampa
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Calendario Appuntamenti - ${currentWeekStart.toLocaleDateString('it-IT')} - ${weekEnd.toLocaleDateString('it-IT')}</title>
        <style>
          @media print {
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            @page { size: landscape; margin: 1cm; }
          }
          body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
          h1 { text-align: center; color: #1976d2; margin-bottom: 10px; }
          .header { text-align: center; margin-bottom: 20px; color: #666; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; vertical-align: top; }
          th { background-color: #1976d2; color: white; font-weight: bold; }
          .time-slot { font-weight: bold; color: #333; min-width: 80px; }
          .appointment { margin-bottom: 10px; padding: 5px; background: #f5f5f5; border-radius: 4px; }
          .patient-name { font-weight: bold; color: #1976d2; }
          .service { color: #666; font-size: 0.9em; }
          .details { font-size: 0.85em; color: #888; margin-top: 3px; }
          .status { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 0.8em; }
          .status-confirmed { background: #4caf50; color: white; }
          .status-pending { background: #ff9800; color: white; }
          .status-cancelled { background: #f44336; color: white; }
          .no-appointments { text-align: center; color: #999; font-style: italic; padding: 20px; }
        </style>
      </head>
      <body>
        <h1>📅 Calendario Appuntamenti</h1>
        <div class="header">
          <div>Periodo: ${currentWeekStart.toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} - ${weekEnd.toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          <div>Agende: ${diaryIds}</div>
          <div>Stampato il: ${new Date().toLocaleString('it-IT')}</div>
        </div>
        ${generatePrintTable(eventsInRange)}
        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;

    // Apri una nuova finestra e stampa
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
    }
  };

  /**
   * Genera la tabella HTML per la stampa
   */
  const generatePrintTable = (eventsInRange: BookingEvent[]) => {
    if (eventsInRange.length === 0) {
      return '<div class="no-appointments">Nessun appuntamento nel periodo selezionato</div>';
    }

    // Raggruppa eventi per giorno
    const eventsByDay: { [key: string]: BookingEvent[] } = {};
    eventsInRange.forEach(event => {
      const dayKey = new Date(event.start).toLocaleDateString('it-IT');
      if (!eventsByDay[dayKey]) {
        eventsByDay[dayKey] = [];
      }
      eventsByDay[dayKey].push(event);
    });

    // Ordina i giorni
    const sortedDays = Object.keys(eventsByDay).sort((a, b) => {
      return new Date(a.split('/').reverse().join('-')).getTime() - new Date(b.split('/').reverse().join('-')).getTime();
    });

    let tableHTML = '<table>';
    tableHTML += '<thead><tr><th>Data/Ora</th><th>Paziente</th><th>Prestazione</th><th>Dettagli</th><th>Stato</th></tr></thead>';
    tableHTML += '<tbody>';

    sortedDays.forEach(day => {
      const dayEvents = eventsByDay[day].sort((a, b) =>
        new Date(a.start).getTime() - new Date(b.start).getTime()
      );

      dayEvents.forEach((event, index) => {
        const date = new Date(event.start);
        const timeStr = date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
        const dateStr = index === 0 ? date.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }) : '';

        // I dati sono sia a livello root che in extendedProps
        const patientName = event.patientName || event.extendedProps?.patientName || event.extendedProps?.person?.name || 'N/D';
        const serviceName = event.serviceName || event.extendedProps?.serviceName || event.extendedProps?.service?.name || 'N/D';
        const fiscalCode = event.patientFiscalCode || event.extendedProps?.patientFiscalCode || event.extendedProps?.fiscalCode || '';
        const nre = event.extendedProps?.service?.prescription?.nre || '';
        const bookingCode = event.bookingCode || event.extendedProps?.bookingCode || event.extendedProps?.code || '';

        // Gestisci status che potrebbe essere string o object
        const statusObj = event.status || event.extendedProps?.status;
        const status = typeof statusObj === 'string' ? statusObj : (statusObj?.name || statusObj?.description || 'N/D');

        let statusClass = 'status';
        const statusLower = status.toLowerCase();
        if (statusLower.includes('conferm')) statusClass += ' status-confirmed';
        else if (statusLower.includes('annull')) statusClass += ' status-cancelled';
        else statusClass += ' status-pending';

        tableHTML += '<tr>';
        tableHTML += `<td class="time-slot">${dateStr ? '<strong>' + dateStr + '</strong><br>' : ''}${timeStr}</td>`;
        tableHTML += `<td><div class="patient-name">${patientName}</div>${fiscalCode ? '<div class="details">CF: ' + fiscalCode + '</div>' : ''}</td>`;
        tableHTML += `<td><div class="service">${serviceName}</div></td>`;
        tableHTML += `<td class="details">${nre ? 'NRE: ' + nre + '<br>' : ''}${bookingCode ? 'Codice: ' + bookingCode : ''}</td>`;
        tableHTML += `<td><span class="${statusClass}">${status}</span></td>`;
        tableHTML += '</tr>';
      });
    });

    tableHTML += '</tbody></table>';
    return tableHTML;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            📅 Calendario Appuntamenti
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editMode}
                  onChange={(e) => setEditMode(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {editMode ? <Edit /> : <Visibility />}
                  <Typography>{editMode ? 'Modalità Modifica' : 'Sola Lettura'}</Typography>
                </Box>
              }
            />

            <Tooltip title="Stampa Calendario">
              <IconButton onClick={handlePrintCalendar} disabled={loading} color="primary">
                <Print />
              </IconButton>
            </Tooltip>

            <Tooltip title="Ricarica">
              <IconButton onClick={loadBookings} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filtri */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ID Agende (separati da virgola)"
                  value={diaryIds}
                  onChange={(e) => setDiaryIds(e.target.value)}
                  placeholder="9025125,10206325"
                  helperText="Inserisci uno o più ID agenda separati da virgola"
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {/* Navigazione settimana */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton onClick={handlePrevWeek} disabled={loading} size="small">
                      <ChevronLeft />
                    </IconButton>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Today />}
                      onClick={handleToday}
                      disabled={loading}
                    >
                      Oggi
                    </Button>

                    <IconButton onClick={handleNextWeek} disabled={loading} size="small">
                      <ChevronRight />
                    </IconButton>
                  </Box>

                  {/* Controlli Zoom */}
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', borderLeft: 1, borderColor: 'divider', pl: 1 }}>
                    <Tooltip title="Zoom Out (slot più grandi)">
                      <span>
                        <IconButton
                          onClick={handleZoomOut}
                          disabled={loading || slotDuration === '01:00:00'}
                          size="small"
                        >
                          <ZoomOut />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Chip
                      label={getSlotDurationLabel()}
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: '60px', fontWeight: 'bold' }}
                    />

                    <Tooltip title="Zoom In (slot più piccoli)">
                      <span>
                        <IconButton
                          onClick={handleZoomIn}
                          disabled={loading || slotDuration === '00:05:00'}
                          size="small"
                        >
                          <ZoomIn />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  <Button
                    variant="contained"
                    size="small"
                    onClick={loadBookings}
                    disabled={loading}
                  >
                    Carica
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Info settimana corrente */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={`${calendarService.formatDateForFilter(currentWeekStart)} - ${calendarService.formatDateForFilter(getWeekEnd(currentWeekStart))}`}
                color="primary"
                variant="outlined"
              />
              <Chip label={`${events.length} appuntamenti`} color="info" />
              {editMode && (
                <Chip label="MODALITÀ MODIFICA ATTIVA" color="warning" />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Messaggi */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Calendario */}
        <Card>
          <CardContent>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            )}

            <FullCalendar
              ref={calendarRef}
              plugins={[timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              locale={itLocale}
              headerToolbar={false} // Usiamo i nostri controlli
              height="auto"
              slotMinTime="07:00:00"
              slotMaxTime="20:00:00"
              slotDuration={slotDuration}
              slotLabelInterval={slotDuration}
              allDaySlot={false}
              events={events}
              editable={editMode}
              selectable={editMode}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              firstDay={1} // Lunedì
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              select={handleDateSelect}
              eventContent={(eventInfo) => {
                const event = eventInfo.event;
                const extProps = event.extendedProps?.extendedProps || event.extendedProps;

                const tooltipContent = (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {extProps.patientName || 'Paziente'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      📋 {extProps.serviceName || 'Prestazione'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      ⏰ {eventInfo.timeText}
                    </Typography>
                    {extProps.patientFiscalCode && (
                      <Typography variant="caption" display="block">
                        🆔 CF: {extProps.patientFiscalCode}
                      </Typography>
                    )}
                    {extProps.service?.prescription?.nre && (
                      <Typography variant="caption" display="block">
                        📝 NRE: {extProps.service.prescription.nre}
                      </Typography>
                    )}
                    {extProps.entryDateTime && (
                      <Typography variant="caption" display="block">
                        📅 Prenotato: {new Date(extProps.entryDateTime).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    )}
                    {extProps.status && (
                      <Typography variant="caption" display="block">
                        📌 {extProps.status}
                      </Typography>
                    )}
                  </Box>
                );

                return (
                  <Tooltip title={tooltipContent} arrow placement="top" enterDelay={300}>
                    <Box sx={{ p: 0.5, overflow: 'hidden', fontSize: '0.75rem', cursor: 'pointer', height: '100%' }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', lineHeight: 1.2 }}>
                        {eventInfo.timeText}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {event.title}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              }}
            />
          </CardContent>
        </Card>

        {/* Info modalità */}
        <Box sx={{ mt: 2 }}>
          <Alert severity="info">
            {editMode ? (
              <>
                <strong>Modalità Modifica:</strong> Trascina gli appuntamenti per spostarli,
                doppio click per modificare i dettagli, seleziona uno slot vuoto per creare un nuovo appuntamento.
              </>
            ) : (
              <>
                <strong>Modalità Sola Lettura:</strong> Doppio click su un appuntamento per visualizzarne i dettagli.
                Attiva la "Modalità Modifica" per modificare o creare appuntamenti.
              </>
            )}
          </Alert>
        </Box>

        {/* Modali */}
        <BookingDetailsModal
          open={detailsModalOpen}
          booking={selectedEvent}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedEvent(null);
          }}
        />

        <BookingEditModal
          open={editModalOpen}
          booking={selectedEvent || (pendingChange?.type === 'create' ? { ...pendingChange.newData } as BookingEvent : null)}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedEvent(null);
            setPendingChange(null);
          }}
          onSave={handleSaveBooking}
          onDelete={handleDeleteBooking}
        />

        <BookingConfirmModal
          open={confirmModalOpen}
          changeType={pendingChange?.type || 'move'}
          oldData={pendingChange?.oldData}
          newData={pendingChange?.newData}
          onConfirm={handleConfirmChange}
          onCancel={() => {
            setConfirmModalOpen(false);
            setPendingChange(null);
          }}
        />
      </Box>
    </Container>
  );
};

export default CalendarPage;
